import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Fade,
  useTheme
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon
} from '@mui/icons-material';

/**
 * ProgressiveChainOfThoughtDisplay Component
 * 
 * Shows Chain-of-Thought reasoning as it progresses through each step,
 * creating a real-time thinking experience for document/presentation generation.
 */

const ProgressiveChainOfThoughtDisplay = ({ 
  reasoning, 
  isComplete = false, 
  currentStep = 0,
  isUserMessage = false,
  compact = true  // Default to compact for chat consistency
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);  // Default to collapsed
  const [displayedSteps, setDisplayedSteps] = useState([]);
  const [animationStep, setAnimationStep] = useState(0);

  // Parse the reasoning into steps
  const parseReasoningSteps = (reasoningText) => {
    if (!reasoningText) return [];
    
    const steps = [];
    const lines = reasoningText.split('\n');
    let currentStep = null;
    let currentContent = [];

    for (const line of lines) {
      const trimmed = line.trim();
      
      if (trimmed.startsWith('**Step ') || trimmed.startsWith('**Final Answer')) {
        // Save previous step
        if (currentStep) {
          steps.push({
            title: currentStep,
            content: currentContent.join('\n').trim(),
            isComplete: false
          });
        }
        
        // Start new step
        currentStep = trimmed.replace(/\*\*/g, '').replace(':', '');
        currentContent = [];
      } else if (trimmed && currentStep) {
        currentContent.push(trimmed);
      }
    }
    
    // Add the last step
    if (currentStep) {
      steps.push({
        title: currentStep,
        content: currentContent.join('\n').trim(),
        isComplete: false
      });
    }

    return steps;
  };

  const allSteps = parseReasoningSteps(reasoning);

  // This component is only ever rendered with isComplete=true, after the
  // real live progress (see the "step" SSE events in useChatActions.js and
  // the loading indicator in ChatMessage.js) has already shown the user each
  // stage as it actually happened. Re-animating a fake reveal of the same
  // steps here - as if they were only now happening - is exactly the
  // "adding stuff to the chat" behavior that's misleading, so completed
  // reasoning renders immediately instead of trickling in on a timer.
  useEffect(() => {
    if (allSteps.length === 0) return;

    if (isComplete) {
      setDisplayedSteps(allSteps.map(step => ({ ...step, isComplete: true })));
      return;
    }

    const timer = setInterval(() => {
      setDisplayedSteps(prev => {
        if (prev.length < Math.min(currentStep + 1, allSteps.length)) {
          const newSteps = [...prev];
          const nextIndex = prev.length;
          if (nextIndex < allSteps.length) {
            newSteps.push({
              ...allSteps[nextIndex],
              isComplete: nextIndex < currentStep
            });
          }
          return newSteps;
        }
        return prev;
      });

      if (displayedSteps.length >= Math.min(currentStep + 1, allSteps.length)) {
        clearInterval(timer);
      }
    }, 800); // Show each step after 800ms

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSteps.length, currentStep, isComplete]);

  // Mark steps as complete based on current progress
  useEffect(() => {
    setDisplayedSteps(prev => 
      prev.map((step, index) => ({
        ...step,
        isComplete: index < currentStep || isComplete
      }))
    );
  }, [currentStep, isComplete]);

  if (!reasoning || reasoning.trim() === '') {
    return null;
  }

  const getStepIcon = (step, index) => {
    if (step.isComplete) {
      return <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />;
    } else if (index === currentStep && !isComplete) {
      return <PendingIcon sx={{ color: '#3b82f6', fontSize: 20 }} />;
    } else {
      return <PendingIcon sx={{ color: '#64748b', fontSize: 20 }} />;
    }
  };

  const getStepColor = (step, index) => {
    if (step.isComplete) {
      return '#10b981';
    } else if (index === currentStep && !isComplete) {
      return '#3b82f6';
    } else {
      return '#64748b';
    }
  };

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Typography 
        variant="body2" 
        sx={{ 
          color: 'rgba(107, 114, 128, 0.7)',
          fontStyle: 'italic',
          mb: 0.5
        }}
      >
        💭 Chain-of-thought reasoning ({displayedSteps.length}/{allSteps.length} steps)
      </Typography>
      
      {displayedSteps.map((step, index) => (
        <Typography 
          key={index}
          variant="body2" 
          sx={{ 
            color: 'rgba(107, 114, 128, 0.8)',
            lineHeight: 1.5,
            mb: 0.5,
            pl: 2
          }}
        >
          <strong>{step.title}:</strong> {step.content}
        </Typography>
      ))}
    </Box>
  );
};

export default ProgressiveChainOfThoughtDisplay;
